import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api.js';
import { PageHeader } from '@/components/PageHeader.jsx';
import { Button } from '@/components/ui/Button.jsx';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose } from '@/components/ui/Dialog.jsx';
import { FormField, Input } from '@/components/ui/Input.jsx';
import { Badge } from '@/components/ui/Badge.jsx';
import { useToast } from '@/components/ui/Toast.jsx';
import { usePeriodStore } from '@/stores/period.js';
import { formatLKR, rupeesToCents, centsToRupees } from '@/lib/format.js';
import { Lock, Pencil, RotateCcw, Search, Upload } from 'lucide-react';
import { AllowanceImportDialog } from '@/components/AllowanceImportDialog.jsx';

export default function DeductionsMonthPage() {
  const { year, month } = usePeriodStore();
  const qc = useQueryClient();
  const { toast } = useToast();

  const [editing, setEditing] = useState(null); // { emp, comp, assignment }
  const [form, setForm] = useState({ amount: '', percent: '', notes: '' });
  const [search, setSearch] = useState('');
  const [importOpen, setImportOpen] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['currentDeductions', year, month],
    queryFn: () => api.get(`/payroll/current-deductions?year=${year}&month=${month}`),
  });

  const upsertOverride = useMutation({
    mutationFn: (body) => api.put('/payroll/allowance-overrides', body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['currentDeductions', year, month] });
      setEditing(null);
      toast({ variant: 'success', title: 'Deduction override saved' });
    },
    onError: (e) => toast({ variant: 'error', title: 'Failed', description: e.message }),
  });

  const resetOverride = useMutation({
    mutationFn: (overrideId) => api.del(`/payroll/allowance-overrides/${overrideId}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['currentDeductions', year, month] });
      setEditing(null);
      toast({ variant: 'success', title: 'Override removed — reverted to default' });
    },
    onError: (e) => toast({ variant: 'error', title: 'Failed', description: e.message }),
  });

  const openEdit = (emp, comp, assignment) => {
    setForm({
      amount: assignment ? centsToRupees(assignment.amount_cents) : '',
      percent: assignment?.percent ?? '',
      notes: '',
    });
    setEditing({ emp, comp, assignment });
  };

  const handleSave = () => {
    const body = { year, month, employee_id: editing.emp.id, component_id: editing.comp.id };
    if (editing.comp.calc_method === 'fixed') {
      body.amount_cents = rupeesToCents(Number(form.amount));
    } else {
      body.percent = Number(form.percent);
    }
    if (form.notes) body.notes = form.notes;
    upsertOverride.mutate(body);
  };

  const handleReset = () => {
    if (!editing?.assignment?.override_id) return;
    resetOverride.mutate(editing.assignment.override_id);
  };

  if (isLoading) return <div className="p-4">Loading…</div>;

  const { components = [], employees = [] } = data || {};
  const filteredEmployees = search.trim()
    ? employees.filter((e) => {
        const q = search.toLowerCase();
        return e.full_name.toLowerCase().includes(q) || e.emp_no.toLowerCase().includes(q) || (e.department_name || '').toLowerCase().includes(q);
      })
    : employees;

  return (
    <div className="space-y-4">
      <PageHeader
        title="Current Month Deductions"
        description={`${new Date(year, month - 1).toLocaleString('default', { month: 'long' })} ${year} — all active employees`}
        actions={
          <Button variant="outline" onClick={() => setImportOpen(true)}>
            <Upload className="h-4 w-4 mr-2" />
            Import
          </Button>
        }
      />

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
        <input
          type="text"
          placeholder="Filter by name, employee no, or department…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full pl-9 pr-4 py-2 text-sm border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-brand-500"
        />
      </div>

      <div className="overflow-x-auto rounded-lg border border-slate-200 dark:border-slate-700">
        <table className="min-w-full text-sm">
          <thead className="bg-slate-50 dark:bg-slate-800 text-slate-600 dark:text-slate-300">
            <tr>
              <th className="sticky left-0 z-10 bg-slate-50 dark:bg-slate-800 px-4 py-3 text-left font-semibold whitespace-nowrap">Employee</th>
              {components.map((c) => (
                <th key={c.id} className="px-4 py-3 text-right font-semibold whitespace-nowrap">
                  <span className="font-mono text-xs text-slate-500 block">{c.code}</span>
                  <span>{c.name}</span>
                  {!c.is_editable && <Lock className="inline h-3 w-3 ml-1 text-slate-400" />}
                </th>
              ))}
              <th className="px-4 py-3 text-right font-semibold whitespace-nowrap">Total Deductions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
            {filteredEmployees.length === 0 && (
              <tr>
                <td colSpan={components.length + 2} className="text-center text-slate-500 py-6">
                  {employees.length === 0 ? 'No active employees found' : 'No employees match the filter'}
                </td>
              </tr>
            )}
            {filteredEmployees.map((emp) => {
              let total = 0;
              return (
                <tr key={emp.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/50">
                  <td className="sticky left-0 z-10 bg-white dark:bg-slate-900 px-4 py-2 whitespace-nowrap">
                    <div className="font-medium">{emp.full_name}</div>
                    <div className="text-xs text-slate-500 font-mono">{emp.emp_no}{emp.department_name ? ` · ${emp.department_name}` : ''}</div>
                  </td>
                  {components.map((comp) => {
                    const assignment = emp.deductions[comp.id];
                    let displayAmount = 0;
                    if (assignment) {
                      displayAmount = comp.calc_method === 'percent_of_basic'
                        ? Math.round((emp.basic_salary_cents * (assignment.percent || 0)) / 100)
                        : (assignment.amount_cents || 0);
                      total += displayAmount;
                    }
                    return (
                      <td key={comp.id} className="px-4 py-2 text-right whitespace-nowrap tabular-nums font-mono">
                        {assignment ? (
                          <span className="inline-flex items-center justify-end gap-1 group">
                            {assignment.is_overridden && (
                              <span className="h-1.5 w-1.5 rounded-full bg-amber-400 shrink-0" title="Month-specific override active" />
                            )}
                            <span className={assignment.is_overridden ? 'text-amber-600 dark:text-amber-400 font-medium' : ''}>
                              {formatLKR(displayAmount)}
                            </span>
                            {comp.is_editable ? (
                              <button
                                type="button"
                                className="opacity-0 group-hover:opacity-100 transition-opacity p-0.5 rounded hover:bg-slate-200 dark:hover:bg-slate-700"
                                onClick={() => openEdit(emp, comp, assignment)}
                                title="Edit"
                              >
                                <Pencil className="h-3 w-3 text-slate-500" />
                              </button>
                            ) : (
                              <Lock className="h-3 w-3 text-slate-300 opacity-0 group-hover:opacity-100" />
                            )}
                          </span>
                        ) : comp.is_editable ? (
                          <span className="inline-flex items-center justify-end gap-1 group">
                            <span className="text-slate-300">—</span>
                            <button
                              type="button"
                              className="opacity-0 group-hover:opacity-100 transition-opacity p-0.5 rounded hover:bg-slate-200 dark:hover:bg-slate-700"
                              onClick={() => openEdit(emp, comp, null)}
                              title="Add override"
                            >
                              <Pencil className="h-3 w-3 text-slate-400" />
                            </button>
                          </span>
                        ) : (
                          <span className="text-slate-300">—</span>
                        )}
                      </td>
                    );
                  })}
                  <td className="px-4 py-2 text-right font-semibold tabular-nums font-mono whitespace-nowrap text-red-600 dark:text-red-400">
                    {formatLKR(total)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Edit Dialog */}
      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Deduction — {new Date(year, month - 1).toLocaleString('default', { month: 'long' })} {year}</DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div className="rounded-md bg-slate-50 dark:bg-slate-800 px-3 py-2 text-sm space-y-1">
                <div><span className="text-slate-500">Employee: </span><span className="font-medium">{editing.emp.full_name}</span></div>
                <div><span className="text-slate-500">Component: </span><span className="font-mono font-medium">{editing.comp.code}</span> — {editing.comp.name}</div>
                {editing.assignment?.is_overridden && (
                  <div className="pt-1">
                    <Badge variant="warning">Month Override Active</Badge>
                    <span className="ml-2 text-xs text-slate-500">Overrides the standing assignment for this month only</span>
                  </div>
                )}
                {!editing.assignment?.is_overridden && (
                  <div className="pt-1 text-xs text-slate-400">Saving will create a month-specific override without changing the standing assignment</div>
                )}
              </div>
              {editing.comp.calc_method === 'fixed' ? (
                <FormField label="Amount (LKR)">
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={form.amount}
                    onChange={(e) => setForm({ ...form, amount: e.target.value })}
                  />
                </FormField>
              ) : (
                <FormField label="Percent of Basic (%)">
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    max="500"
                    value={form.percent}
                    onChange={(e) => setForm({ ...form, percent: e.target.value })}
                  />
                </FormField>
              )}
              <FormField label="Notes (optional)">
                <Input
                  type="text"
                  placeholder="Reason for override…"
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                />
              </FormField>
            </div>
          )}
          <DialogFooter>
            {editing?.assignment?.is_overridden && (
              <Button
                variant="outline"
                onClick={handleReset}
                disabled={resetOverride.isPending}
                className="mr-auto text-red-600 border-red-200 hover:bg-red-50 dark:hover:bg-red-950"
              >
                <RotateCcw className="h-3.5 w-3.5 mr-1.5" />
                {resetOverride.isPending ? 'Resetting…' : 'Reset to Default'}
              </Button>
            )}
            <DialogClose asChild><Button variant="outline">Cancel</Button></DialogClose>
            <Button onClick={handleSave} disabled={upsertOverride.isPending}>
              {upsertOverride.isPending ? 'Saving…' : 'Save Override'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AllowanceImportDialog
        open={importOpen}
        onOpenChange={setImportOpen}
        type="deduction"
        onSuccess={(res) => {
          qc.invalidateQueries({ queryKey: ['currentDeductions', year, month] });
          toast({
            variant: 'success',
            title: `${res.imported} deduction${res.imported !== 1 ? 's' : ''} imported`,
            description: res.errors?.length ? `${res.errors.length} row(s) failed — see dialog for details` : undefined,
          });
        }}
      />
    </div>
  );
}
